'use client'

import { useCallback, useState } from 'react'

import Link from 'next/link'

import { CopyHelpText } from './CopyHelpText'

export type AnchoredHeadingProps = {
  /**
   * The child JSX, intended to be the Heading element (H2, H3, etc.).
   */
  children: React.ReactNode
  /**
   * The id of the heading. This is used to create the link to the heading.
   */
  headingId: string | undefined
}

export const AnchoredHeading: React.FC<AnchoredHeadingProps> = ({
  children,
  headingId,
}) => {
  const [isCopied, setIsCopied] = useState(false)

  // Copy the link to the heading and set the copied state to true
  const onClick = useCallback(() => {
    const url = `${window.location.origin}${window.location.pathname}#${headingId}`
    try {
      void navigator.clipboard.writeText(url)
      setIsCopied(true)

      // Reset the copied state after 3 seconds
      setTimeout(() => {
        setIsCopied(false)
      }, 3000)
    } catch (error) {
      console.error('Failed to copy URL to clipboard:', error)
    }
  }, [headingId])

  // If no headingId is provided, return the heading element unaltered
  if (!headingId) return <>{children}</>

  return (
    <button className="group" data-testid="anchored-heading" onClick={onClick}>
      <Link
        href={{
          hash: headingId,
        }}
      >
        <div className="flex flex-wrap items-center gap-2 text-left">
          <div className="group-hover:underline">{children}</div>
          <CopyHelpText isCopied={isCopied} />
        </div>
      </Link>
    </button>
  )
}
